<template>
  <div class="device-management">
    <!-- 顶部横幅 -->
    <div class="hero-banner">
      <div class="hero-bg">
        <div class="hero-orb hero-orb-1"></div>
        <div class="hero-orb hero-orb-2"></div>
        <div class="hero-orb hero-orb-3"></div>
      </div>
      <div class="hero-content">
        <div class="hero-icon">
          <el-icon :size="28" color="#fff"><Monitor /></el-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">终端设备管理</h1>
          <p class="hero-subtitle">
            管理设备硬件编号 · 控制终端接入 · 监控激活状态
          </p>
        </div>
        <div class="hero-actions">
          <el-button
            class="hero-btn hero-btn-refresh"
            size="large"
            :loading="loading"
            @click="loadDevices"
          >
            <el-icon><Refresh /></el-icon><span>刷新</span>
          </el-button>
          <el-button class="hero-btn" size="large" @click="openPreRegister">
            <el-icon><Plus /></el-icon><span>录入终端设备</span>
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-row">
      <div
        v-for="(stat, i) in statsList"
        :key="i"
        class="stat-card"
        :class="stat.cls"
        :style="{ animationDelay: i * 0.1 + 's' }"
      >
        <div class="stat-glow"></div>
        <div class="stat-icon-wrap">
          <el-icon :size="22" :color="stat.color">
            <Monitor v-if="stat.icon === 'Monitor'" />
            <CircleCheck v-else-if="stat.icon === 'CircleCheck'" />
            <Clock v-else-if="stat.icon === 'Clock'" />
            <CircleClose v-else-if="stat.icon === 'CircleClose'" />
          </el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
        <div class="stat-trend">
          <div class="stat-bar" :style="{ width: stat.barWidth }"></div>
        </div>
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-section">
      <div class="search-input-wrapper">
        <el-input
          v-model="searchKeyword"
          placeholder="输入 deviceId 或名称搜索..."
          clearable
          :prefix-icon="Search"
        />
      </div>
      <div class="search-info">
        共
        <strong>{{
          filteredBatches.reduce((s, b) => s + b.devices.length, 0)
        }}</strong>
        台终端 · <strong>{{ filteredBatches.length }}</strong> 个证书批次
      </div>
    </div>

    <!-- 批次卡片列表 -->
    <div v-loading="loading" class="batch-list">
      <el-empty
        v-if="!loading && filteredBatches.length === 0"
        description="暂无终端设备"
        class="empty-state"
      >
        <el-button type="primary" @click="openPreRegister">立即录入</el-button>
      </el-empty>

      <div
        v-for="(batch, idx) in filteredBatches"
        :key="batch.batchId"
        class="batch-card"
        :style="{ animationDelay: idx * 0.08 + 's' }"
      >
        <!-- 批次头部 -->
        <div class="batch-header">
          <div class="batch-left">
            <div class="batch-icon">
              <el-icon :size="18" color="#fff"><DocumentChecked /></el-icon>
            </div>
            <div class="batch-meta">
              <div class="batch-title">
                <span class="batch-prefix">证书</span>
                <span class="batch-code">{{ batch.batchCode }}</span>
                <span v-if="batch.batchCreateTime" class="batch-time">
                  <el-icon :size="13"><Clock /></el-icon>
                  {{ formatBatchTime(batch.batchCreateTime) }}
                </span>
              </div>
              <div class="batch-stats">
                <span class="batch-stat-item stat-active"
                  >已激活 {{ batch.activatedCount }}</span
                >
                <span
                  v-if="batch.disabledCount > 0"
                  class="batch-stat-item stat-disabled"
                  >已禁用 {{ batch.disabledCount }}</span
                >
                <span class="batch-stat-item stat-total"
                  >共 {{ batch.devices.length }} 台</span
                >
              </div>
            </div>
          </div>
          <div class="batch-actions">
            <el-button
              class="add-device-btn"
              size="default"
              plain
              @click="openAddDevices(batch)"
            >
              <el-icon><Plus /></el-icon>
              <span>添加设备</span>
            </el-button>
            <el-button
              class="download-btn"
              size="default"
              @click="downloadCert(batch.batchId, batch.batchCode)"
            >
              <el-icon><Download /></el-icon>
              <span>下载证书</span>
            </el-button>
          </div>
        </div>

        <!-- 设备列表 -->
        <div class="device-list">
          <div class="device-row device-row-head">
            <div class="device-cell cell-id">
              <span class="head-label">终端编号 deviceId</span>
            </div>
            <div class="device-cell cell-name">
              <span class="head-label">终端名称</span>
            </div>
            <div class="device-cell cell-channel">
              <span class="head-label">频道</span>
            </div>
            <div class="device-cell cell-status">
              <span class="head-label">状态</span>
            </div>
            <div class="device-cell cell-actions">
              <span class="head-label">操作</span>
            </div>
          </div>
          <div
            v-for="row in batch.devices"
            :key="row.endpointId"
            class="device-row"
            :class="{
              'is-disabled': row.enabled === 0,
            }"
          >
            <div class="device-cell cell-id">
              <div class="device-id-dot"></div>
              <span class="device-id-text">{{ row.deviceId }}</span>
            </div>
            <div class="device-cell cell-name">{{ row.name || "—" }}</div>
            <div class="device-cell cell-channel">
              <span class="channel-badge">频道{{ row.channel || 1 }}</span>
            </div>
            <div class="device-cell cell-status">
              <span class="status-pill" :class="getStatusClass(row)">
                <span class="pulse"></span>
                {{ getStatusText(row) }}
              </span>
            </div>
            <div class="device-cell cell-actions">
              <!-- 主操作行：禁用/启用，可能带删除 -->
              <div class="action-row action-row-main">
                <button
                  v-if="row.enabled !== 0"
                  class="action-pill pill-danger"
                  @click="toggleDevice(row)"
                >
                  <el-icon><VideoPause /></el-icon>禁用
                </button>
                <template v-else>
                  <button
                    class="action-pill pill-success"
                    @click="toggleDevice(row)"
                  >
                    <el-icon><VideoPlay /></el-icon>启用
                  </button>
                  <button
                    class="action-pill pill-danger"
                    @click="deleteDevice(row)"
                  >
                    <el-icon><Delete /></el-icon>删除
                  </button>
                </template>
              </div>

              <!-- 辅助操作行：换硬件 + 换频道（始终显示）+ 迁移设备（绑定船舶才显示） -->
              <div class="action-row action-row-sub">
                <button
                  class="action-pill pill-amber"
                  @click="openReplaceHardware(row)"
                >
                  <el-icon><RefreshRight /></el-icon>换硬件
                </button>
                <button
                  class="action-pill pill-blue"
                  @click="openChangeChannel(row)"
                >
                  <el-icon><Switch /></el-icon>换频道
                </button>
                <button
                  v-if="row.shipId"
                  class="action-pill pill-green"
                  :class="{ 'pill-disabled': row.status !== 'ONLINE' }"
                  :disabled="row.status !== 'ONLINE'"
                  :title="
                    row.status !== 'ONLINE'
                      ? '终端离线，新终端上线后可迁移设备协议'
                      : '将船舶设备协议文件迁移到该终端'
                  "
                  @click="openMigrate(row)"
                >
                  <el-icon><Box /></el-icon>迁移设备
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PreRegisterDevicesDialog
      v-model="preRegisterVisible"
      :cert-batch-id="preRegisterBatchId"
      :batch-code="preRegisterBatchCode"
      @success="loadDevices"
    />
    <ReplaceHardwareDialog
      v-model="replaceHardwareVisible"
      :endpoint-id="replaceHardwareEndpointId"
      :current-device-id="replaceHardwareDeviceId"
      :current-name="replaceHardwareName"
      @success="loadDevices"
    />
    <ChangeChannelDialog
      v-model="changeChannelVisible"
      :endpoint-id="changeChannelEndpointId"
      :current-channel="changeChannelCurrentChannel"
      :device-id="changeChannelDeviceId"
      @success="loadDevices"
    />
    <MigrateDeviceProtocolsDialog
      v-model="migrateVisible"
      :endpoint-id="migrateEndpointId"
      :device-id="migrateDeviceId"
      :ship-id="migrateShipId"
      :ship-name="migrateShipName"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Monitor,
  Plus,
  Search,
  DocumentChecked,
  Download,
  CircleCheck,
  Clock,
  CircleClose,
  VideoPause,
  VideoPlay,
  Refresh,
  RefreshRight,
  Delete,
  Switch,
  Box,
} from "@element-plus/icons-vue";
import {
  getAllDevicesApi,
  deleteDeviceApi,
  toggleEndpointStatusApi,
  downloadCertBatchPackageApi,
  type DeviceItem,
} from "@/api/cert/certBatchApi";
import PreRegisterDevicesDialog from "./PreRegisterDevicesDialog.vue";
import ReplaceHardwareDialog from "./ReplaceHardwareDialog.vue";
import ChangeChannelDialog from "./ChangeChannelDialog.vue";
import MigrateDeviceProtocolsDialog from "./MigrateDeviceProtocolsDialog.vue";

const loading = ref(false);
const deviceList = ref<DeviceItem[]>([]);
const searchKeyword = ref("");

const statsList = computed(() => {
  const total = deviceList.value.length;
  const active = deviceList.value.filter(
    (d) => d.activated && d.enabled !== 0
  ).length;
  const disabled = deviceList.value.filter(
    (d) => d.activated && d.enabled === 0
  ).length;
  const max = Math.max(total, 1);
  return [
    {
      label: "总录入",
      value: total,
      color: "#3b82f6",
      icon: "Monitor",
      cls: "stat-blue",
      barWidth: `${(total / max) * 100}%`,
    },
    {
      label: "在线",
      value: active,
      color: "#10b981",
      icon: "CircleCheck",
      cls: "stat-green",
      barWidth: `${(active / max) * 100}%`,
    },
    {
      label: "已禁用",
      value: disabled,
      color: "#ef4444",
      icon: "CircleClose",
      cls: "stat-red",
      barWidth: `${(disabled / max) * 100}%`,
    },
  ];
});

interface BatchGroup {
  batchId: string;
  batchCode: string;
  devices: DeviceItem[];
  activatedCount: number;
  disabledCount: number;
  batchCreateTime: string | null;
}

const filteredBatches = computed<BatchGroup[]>(() => {
  let list = deviceList.value;
  if (searchKeyword.value) {
    const kw = searchKeyword.value.toLowerCase();
    list = list.filter(
      (d) =>
        (d.deviceId && d.deviceId.toLowerCase().includes(kw)) ||
        (d.name && d.name.toLowerCase().includes(kw))
    );
  }

  const map = new Map<string, BatchGroup>();
  for (const d of list) {
    const bid = d.certBatchId || "unknown";
    if (!map.has(bid)) {
      map.set(bid, {
        batchId: bid,
        batchCode: d.batchCode || "未知批次",
        devices: [],
        activatedCount: 0,
        disabledCount: 0,
        batchCreateTime: d.batchCreateTime || null,
      });
    }
    const batch = map.get(bid)!;
    batch.devices.push(d);
    if (d.enabled === 0) batch.disabledCount++;
    else batch.activatedCount++;
  }

  // 按证书批次创建时间倒序排列（最新的在最上面）
  return Array.from(map.values()).sort((a, b) => {
    const ta = a.batchCreateTime ? new Date(a.batchCreateTime).getTime() : 0;
    const tb = b.batchCreateTime ? new Date(b.batchCreateTime).getTime() : 0;
    return tb - ta;
  });
});

const getStatusText = (row: DeviceItem) => {
  if (row.enabled === 0) return "已禁用";
  return "已激活";
};

const getStatusClass = (row: DeviceItem) => {
  if (row.enabled === 0) return "pill-red";
  return "pill-emerald";
};

const loadDevices = async () => {
  loading.value = true;
  const result = await getAllDevicesApi();
  loading.value = false;
  if (result) {
    deviceList.value = result;
  }
};

const toggleDevice = async (row: DeviceItem) => {
  try {
    const result = await toggleEndpointStatusApi(row.endpointId);
    if (result) {
      ElMessage.success(`终端已${result.action}`);
      await loadDevices();
    }
  } catch (e: any) {
    ElMessage.error(e.message || "操作失败");
  }
};

const deleteDevice = async (row: DeviceItem) => {
  try {
    await ElMessageBox.confirm(
      `确认删除已禁用设备 ${row.deviceId}？`,
      "删除设备",
      { type: "warning" }
    );
    const result = await deleteDeviceApi(row.endpointId);
    if (result) {
      ElMessage.success("设备已删除");
      await loadDevices();
    }
  } catch (e: any) {
    if (e !== "cancel") ElMessage.error(e.message || "删除失败");
  }
};

const downloadCert = async (batchId: string, batchCode: string) => {
  try {
    await downloadCertBatchPackageApi(batchId, `${batchCode}-certs.zip`);
    ElMessage.success("证书压缩包下载成功");
  } catch (e) {
    ElMessage.error("证书下载失败");
  }
};

const preRegisterVisible = ref(false);
const preRegisterBatchId = ref("");
const preRegisterBatchCode = ref("");
const replaceHardwareVisible = ref(false);
const replaceHardwareEndpointId = ref("");
const replaceHardwareDeviceId = ref("");
const replaceHardwareName = ref("");

const changeChannelVisible = ref(false);
const changeChannelEndpointId = ref("");
const changeChannelCurrentChannel = ref(1);
const changeChannelDeviceId = ref("");

const openChangeChannel = (row: DeviceItem) => {
  changeChannelEndpointId.value = row.endpointId;
  changeChannelCurrentChannel.value = row.channel || 1;
  changeChannelDeviceId.value = row.deviceId || "";
  changeChannelVisible.value = true;
};

/** 顶部入口：新建批次录入——必须清空残留的批次信息，否则会误入"追加设备"模式
 *  （场景：先点过批次里的"添加设备"再关掉，batchId 残留导致顶部按钮也变成追加弹窗） */
const openPreRegister = () => {
  preRegisterBatchId.value = "";
  preRegisterBatchCode.value = "";
  preRegisterVisible.value = true;
};

const openAddDevices = (batch: { batchId: string; batchCode: string }) => {
  preRegisterBatchId.value = batch.batchId;
  preRegisterBatchCode.value = batch.batchCode;
  preRegisterVisible.value = true;
};

const openReplaceHardware = (row: DeviceItem) => {
  replaceHardwareEndpointId.value = row.endpointId;
  replaceHardwareDeviceId.value = row.deviceId || "";
  replaceHardwareName.value = row.name || "";
  replaceHardwareVisible.value = true;
};

// 迁移设备协议（换硬件配套）
const migrateVisible = ref(false);
const migrateEndpointId = ref("");
const migrateDeviceId = ref("");
const migrateShipId = ref("");
const migrateShipName = ref("");

const openMigrate = (row: DeviceItem) => {
  if (!row.shipId) return;
  migrateEndpointId.value = row.endpointId;
  migrateDeviceId.value = row.deviceId || "";
  migrateShipId.value = row.shipId;
  migrateShipName.value = row.shipName || "";
  migrateVisible.value = true;
};

const formatTime = (time: string) => {
  if (!time) return "";
  const d = new Date(time);
  return d.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatBatchTime = (time: string) => {
  if (!time) return "";
  const d = new Date(time);
  return `创建于 ${d.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })}`;
};

onMounted(() => {
  loadDevices();
});
</script>

<style scoped>
.device-management {
  position: relative;
  padding: 24px 32px 180px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: calc(100vh - 80px);
  box-sizing: border-box;
}

/* ========== 顶部横幅 ========== */
.hero-banner {
  position: relative;
  padding: 36px 32px;
  border-radius: 20px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #6366f1 100%);
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0 10px 40px rgba(59, 130, 246, 0.2);
}
.hero-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}
.hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.5;
}
.hero-orb-1 {
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, #60a5fa, transparent);
  top: -80px;
  left: -60px;
}
.hero-orb-2 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, #a78bfa, transparent);
  bottom: -80px;
  right: 20%;
}
.hero-orb-3 {
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, #22d3ee, transparent);
  top: 30%;
  right: -40px;
}
.hero-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
}
.hero-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.hero-text {
  flex: 1;
}
.hero-title {
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}
.hero-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  letter-spacing: 0.3px;
}
.hero-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}
.hero-btn {
  border-radius: 12px !important;
  padding: 0 24px !important;
  height: 44px !important;
  font-weight: 600 !important;
  background: #fff !important;
  color: #1e3a8a !important;
  border: none !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
}
.hero-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25) !important;
}
.hero-btn-refresh {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: none !important;
}
.hero-btn-refresh:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  box-shadow: none !important;
}

/* ========== 统计卡片 ========== */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}
.stat-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
  border: 1px solid #e8ecf1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  animation: slideUp 0.5s ease both;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 20px 40px rgba(0, 0, 0, 0.06);
}
.stat-glow {
  position: absolute;
  top: -50%;
  right: -20%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  opacity: 0.08;
  filter: blur(40px);
}
.stat-blue .stat-glow {
  background: #3b82f6;
}
.stat-green .stat-glow {
  background: #10b981;
}
.stat-amber .stat-glow {
  background: #f59e0b;
}
.stat-red .stat-glow {
  background: #ef4444;
}
.stat-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.stat-blue .stat-icon-wrap {
  background: linear-gradient(135deg, #dbeafe, #93c5fd);
}
.stat-green .stat-icon-wrap {
  background: linear-gradient(135deg, #d1fae5, #6ee7b7);
}
.stat-amber .stat-icon-wrap {
  background: linear-gradient(135deg, #fef3c7, #fcd34d);
}
.stat-red .stat-icon-wrap {
  background: linear-gradient(135deg, #fee2e2, #fca5a5);
}
.stat-info {
  margin-top: 12px;
}
.stat-value {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -1px;
}
.stat-label {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 2px;
  font-weight: 500;
  letter-spacing: 0.3px;
}
.stat-trend {
  margin-top: 12px;
  height: 3px;
  background: #f1f5f9;
  border-radius: 2px;
  overflow: hidden;
}
.stat-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}
.stat-blue .stat-bar {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}
.stat-green .stat-bar {
  background: linear-gradient(90deg, #10b981, #34d399);
}
.stat-amber .stat-bar {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}
.stat-red .stat-bar {
  background: linear-gradient(90deg, #ef4444, #f87171);
}

/* ========== 搜索区 ========== */
.search-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 14px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #e8ecf1;
}
.search-input-wrapper {
  flex: 1;
  max-width: 400px;
}
.search-input-wrapper :deep(.el-input__wrapper) {
  border-radius: 10px;
}
.search-info {
  font-size: 13px;
  color: #64748b;
}
.search-info strong {
  color: #3b82f6;
  font-weight: 700;
  margin: 0 4px;
}

/* ========== 批次卡片 ========== */
.batch-list {
  min-height: 200px;
  padding-bottom: 60px;
}
.batch-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8ecf1;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.6s ease both;
  transition: all 0.3s ease;
}
.batch-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border-color: #d4dae2;
}

/* 批次头部 */
.batch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e8ecf1;
}
.batch-left {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
}
.batch-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}
.batch-meta {
  flex: 1;
  min-width: 0;
}
.batch-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}
.batch-prefix {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}
.batch-code {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  font-family: "SF Mono", "Monaco", monospace;
  letter-spacing: -0.3px;
}
.batch-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  background: #f1f5f9;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-left: 4px;
}
.batch-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.batch-stat-item {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.stat-active {
  background: #d1fae5;
  color: #047857;
}
.stat-disabled {
  background: #fee2e2;
  color: #b91c1c;
}
.stat-total {
  background: #f1f5f9;
  color: #64748b;
}
.download-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg, #3b82f6, #6366f1) !important;
  border: none !important;
  color: #fff !important;
  padding: 0 18px !important;
  height: 36px !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35) !important;
  transition: all 0.3s ease !important;
}
.batch-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.add-device-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  height: 36px !important;
  padding: 0 16px !important;
  color: #10b981 !important;
  background: #ecfdf5 !important;
  border: 1px solid #a7f3d0 !important;
  transition: all 0.3s ease !important;
}
.add-device-btn:hover {
  background: #d1fae5 !important;
  border-color: #6ee7b7 !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2) !important;
}
.download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.45) !important;
}

/* ========== 设备行 ========== */
.device-list {
  padding: 0 8px;
}
.device-row {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  position: relative;
}
.device-row:last-child {
  border-bottom: none;
}
.device-row-head {
  background: #fafbfc;
  padding: 10px 16px;
  border-bottom: 1px solid #e8ecf1;
}
.device-row-head:hover {
  background: #fafbfc;
}
.head-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
/* 仅操作列表头居中（与下方按钮 group 对齐） */
.cell-actions .head-label {
  width: 100%;
  text-align: center;
}
.device-row:hover {
  background: linear-gradient(90deg, #f8fafc 0%, transparent 100%);
}
.device-row.is-disabled {
  background: linear-gradient(90deg, #fef2f205 0%, transparent 100%);
}
.device-cell {
  flex: 1;
  min-width: 0;
  padding: 0 12px;
  font-size: 13px;
  white-space: nowrap;
  display: flex;
  align-items: center;
}
.cell-id {
  overflow: hidden;
}
.cell-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #64748b;
}
.cell-status {
  justify-content: center;
}
.cell-channel {
  flex: 0 0 80px;
  justify-content: center;
}
.channel-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: #ede9fe;
  color: #6d28d9;
  white-space: nowrap;
}
.pill-blue {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;
}
.pill-blue:hover {
  background: #dbeafe;
  border-color: #93c5fd;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(29, 78, 216, 0.2);
}
.pill-green {
  color: #047857;
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.pill-green:hover {
  background: #d1fae5;
  border-color: #6ee7b7;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(4, 120, 87, 0.2);
}
.pill-green.pill-disabled,
.pill-green:disabled {
  cursor: not-allowed;
  opacity: 0.55;
  transform: none;
  box-shadow: none;
}
.cell-time {
  flex: 0 0 150px;
  color: #94a3b8;
  font-family: "SF Mono", Monaco, monospace;
  font-size: 12px;
}
.cell-actions {
  /* 固定基准宽度，确保表头和表体每一行对齐 */
  flex: 0 1 230px;
  min-width: 0;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
}
.action-row {
  display: inline-flex;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.action-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 5px 10px;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: transparent;
  color: #475569;
  white-space: nowrap;
  line-height: 1.4;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.action-pill + .action-pill {
  border-left: 1px solid rgba(15, 23, 42, 0.08);
}

.device-id-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #3b82f6;
  margin-right: 10px;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  flex-shrink: 0;
}
.device-row.is-disabled .device-id-dot {
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}
.device-id-text {
  font-family: "SF Mono", Monaco, monospace;
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

/* 状态胶囊 */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.pill-emerald {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #047857;
}
.pill-emerald .pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.3);
  animation: pulse-emerald 2s infinite;
}
.pill-amber {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #b45309;
}
.pill-amber .pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f59e0b;
  animation: pulse-amber 1.5s infinite;
}
.pill-red {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #b91c1c;
}
.pill-red .pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ef4444;
}

/* 操作按钮 - button group 样式：每行内按钮贴在一起 */
.action-pill .el-icon {
  font-size: 12px;
}
.action-row {
  background: #f1f5f9;
}
.action-row .action-pill:hover {
  background: rgba(255, 255, 255, 0.8);
  color: #1e293b;
}
.pill-success {
  color: #047857;
}
.pill-success:hover {
  color: #065f46;
  background: #d1fae5;
}
.pill-amber {
  color: #b45309;
}
.pill-amber:hover {
  color: #92400e;
  background: #fde68a;
}
.pill-danger {
  color: #b91c1c;
}
.pill-danger:hover {
  color: #991b1b;
  background: #fecaca;
}

.empty-state {
  padding: 80px 0;
}

/* 动画 */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes pulse-emerald {
  0%,
  100% {
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.15);
  }
}
@keyframes pulse-amber {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
  .hero-content {
    flex-direction: column;
    align-items: flex-start;
  }
  .cell-time {
    display: none;
  }
}
</style>
